/**
 * testMappingService.ts
 * Phase 4 (qa/quote-wo-schedule-lifecycle-2026-07-11/PLAN.md) — frontend
 * service for the Admin → Test Mapping studio.
 *
 * All requests go through apiClient (MSAL auth + error capture). Every
 * endpoint is admin-only on the backend (requireMinRole('admin') on the whole
 * /api/admin/test-catalog-map router).
 *
 *   GET    /admin/test-catalog-map               — whole page in one round trip
 *   GET    /admin/test-catalog-map/suggestions   — fuzzy candidates (bigram Dice)
 *   POST   /admin/test-catalog-map               — create-or-remap
 *   PATCH  /admin/test-catalog-map/:key          — confirm toggle / notes
 *   DELETE /admin/test-catalog-map/:key          — unmap (soft delete)
 */

import apiClient from './apiClient';

// ── Shapes (mirror backend/src/services/testCatalogMapService.ts) ────────────

export interface TestMappingRow {
  TestCatalogMapKey: string;
  TestKey: string;
  TestCatalogItemKey: string;
  IsConfirmed: boolean;
  Notes: string | null;
}

export interface MappingStudioTest {
  TestKey: string;
  TestName: string;
  /** Active TestCatalogMap row, or null when the test has no mapping row. */
  mapping: Pick<TestMappingRow, 'TestCatalogMapKey' | 'TestCatalogItemKey' | 'IsConfirmed' | 'Notes'> | null;
  /** Catalog item resolving by EXACT NAME when no map row exists (still schedulable). */
  exactNameMatchKey: string | null;
  /** Active work-order lines on this lab test — the blast radius of an unmapped test. */
  woLineCount: number;
}

export interface MappingStudioCatalogItem {
  TestCatalogItemKey: string;
  TestName: string;
  /** Dominant LabSection of the item's recipe task types (null = no recipe). */
  LabSection: string | null;
  /** Active recipe rows; 0 = recipe-less (maps but generates no tasks). */
  taskTypeCount: number;
}

export interface MappingStudioData {
  tests: MappingStudioTest[];
  catalogItems: MappingStudioCatalogItem[];
}

export interface MappingSuggestionCandidate {
  testCatalogItemKey: string;
  testName: string;
  /** Bigram-Dice score in [0, 1]. */
  score: number;
}

export interface MappingSuggestion {
  testKey: string;
  testName: string;
  candidates: MappingSuggestionCandidate[];
}

// ── Calls ─────────────────────────────────────────────────────────────────────

/** Fetch the whole studio (tests + catalog items) in one round trip. */
export async function fetchMappingStudio(): Promise<MappingStudioData> {
  const response = await apiClient.get<MappingStudioData>('/admin/test-catalog-map');
  return response.data;
}

/** Fetch fuzzy mapping suggestions for every unresolved test. */
export async function fetchMappingSuggestions(): Promise<MappingSuggestion[]> {
  const response = await apiClient.get<MappingSuggestion[]>('/admin/test-catalog-map/suggestions');
  return response.data;
}

/**
 * Create-or-remap a mapping. The backend transactionally soft-deletes any
 * existing active row for the test. New rows default to unconfirmed unless
 * isConfirmed:true is passed.
 */
export async function createMapping(data: {
  testKey: string;
  testCatalogItemKey: string;
  notes?: string | null;
  isConfirmed?: boolean;
}): Promise<TestMappingRow> {
  const response = await apiClient.post<TestMappingRow>('/admin/test-catalog-map', data);
  return response.data;
}

/** Toggle IsConfirmed and/or edit Notes on an existing mapping. */
export async function updateMapping(
  key: string,
  data: { isConfirmed?: boolean; notes?: string | null },
): Promise<TestMappingRow> {
  const response = await apiClient.patch<TestMappingRow>(`/admin/test-catalog-map/${key}`, data);
  return response.data;
}

/** Unmap (soft delete) a mapping row. */
export async function deleteMapping(key: string): Promise<void> {
  await apiClient.delete(`/admin/test-catalog-map/${key}`);
}
